/**
 * Builder-Modell und Konvertierung zwischen Server-JSON und Editor-Struktur.
 *
 * Die Library bindet sich bewusst nicht an den generierten API-Client einer
 * einzelnen App: Backoffice und AdminPanel haben jeweils einen eigenen, und
 * beide liefern dieselbe Struktur. Die Konverter nehmen das rohe Server-JSON
 * deshalb als `any` entgegen und lesen defensiv; typisiert ist, was sie
 * zurückgeben.
 */

/**
 * Rohes Feld-JSON, wie es der Server liefert.
 *
 * Bewusst strukturell und locker typisiert: Backoffice und AdminPanel haben je
 * einen eigenen generierten API-Client, und der kennt einige Properties bis
 * heute nicht (readonly, folderLevel, exportColumns …). Die Konverter lesen
 * deshalb defensiv; typisiert ist, was sie zurückgeben.
 */
export interface ApiFormFieldJson {
  id?: string;
  type?: string;
  label?: string;
  // Durchgehend `| null`, weil die generierten Clients beider Apps nullable
  // Properties so abbilden und sonst nicht zuweisbar wären.
  hint?: string | null;
  initial?: string | null;
  hidden?: boolean | null;
  order?: number;
  uniqueKey?: string;
  validators?: { type?: string; options?: any }[] | null;
  selectOptions?: { selectableOptions?: { label?: string | null }[] | null } | null;
  numberOptions?: {
    decimalPlaces?: number | null;
    useThousandsSeparator?: boolean | null;
    unit?: string | null;
  } | null;
  photoPickerOptions?: {
    maxPhotos?: number | null;
    showTimestamp?: boolean | null;
    showGeoStamp?: boolean | null;
    writeGeoExif?: boolean | null;
    allowAnnotation?: boolean | null;
    // DOCU-815: im generierten Client beider Apps noch nicht enthalten.
    exportColumns?: number | null;
    exportMaxImageCm?: number | null;
  } | null;
}

/** Rohes Level-JSON, wie es der Server liefert. Siehe [ApiFormFieldJson]. */
export interface ApiFormLevelJson {
  id?: string;
  name?: string;
  description?: string | null;
  titleFieldId?: string | null;
  subtitleFieldId?: string | null;
  exportFormat?: string | null;
  exportTableMarker?: string | null;
  exportKeepTogether?: boolean | null;
  exportHideWhenEmpty?: boolean | null;
  fields?: ApiFormFieldJson[] | null;
  children?: ApiFormLevelJson[] | null;
}

export interface FormField {
  id: string;
  type: string;
  label: string;
  hint?: string;
  required: boolean;
  validators: any[];
  options?: string[];
  maxFiles?: number;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  useThousandsSeparator?: boolean;
  unit?: string;
  allowPhoto?: boolean;
  initial?: string | null;
  showTimestamp?: boolean;
  showGeoStamp?: boolean;
  writeGeoExif?: boolean;
  // DOCU-815: Foto-Export (in FormFieldPhotoOptions). exportColumns = Spalten
  // im DOCX-Raster; exportMaxImageCm = max. lange Kante in cm.
  exportColumns?: number | null;
  exportMaxImageCm?: number | null;
  // DOCU-818: dieses Feld im Export ausblenden, wenn leer.
  exportHideWhenEmpty?: boolean | null;
  allowAnnotation?: boolean;
  hidden?: boolean | null;
  // DOCU-669: Feld in der App nur anzeigen, nicht bearbeitbar (DOCU-667).
  // Das Flag steuert ausschließlich die App; im BackOffice bleibt der Wert
  // editierbar. null/undefined = bearbeitbar.
  readonly?: boolean | null;
  // DOCU-704/746: konfigurierbares Wertepärchen für den Boolean-Export (Checkbox).
  // trueLabel/falseLabel = Text; trueSvg/falseSvg = Inline-SVG-Visual (z. B. Toggle,
  // hat im Export Vorrang vor dem Text).
  checkboxOptions?: { trueLabel?: string; falseLabel?: string; trueSvg?: string; falseSvg?: string };
  // DOCU-745: Ordner-Schlüssel (DOCU-740). 1 = dieses Root-Feld füllt den
  // Ordnernamen (_folderLabel1). null/undefined = kein Ordnerfeld.
  folderLevel?: number | null;
  // DOCU-664: Properties, die der Editor nicht kennt, werden hier roh festgehalten
  // und beim Speichern unverändert wieder eingespielt (Raw-JSON-Roundtrip).
  extra?: Record<string, unknown>;
}

export interface FormLevel {
  id: string;
  name: string;
  description?: string;
  titleFieldId?: string;
  subtitleFieldId?: string;
  // DOCU-664 / DOCU-625: DOCX-Export-Steuerung pro Level (Section).
  exportFormat?: string;
  exportTableMarker?: string;
  // DOCU-815/818: Prüfpunkt auf einer Seite zusammenhalten / leeren Block ausblenden.
  exportKeepTogether?: boolean | null;
  exportHideWhenEmpty?: boolean | null;
  fields: FormField[];
  children: FormLevel[];
  isExpanded: boolean;
  // DOCU-664: unbekannte Level-Properties (Raw-JSON-Roundtrip), siehe FormField.extra.
  extra?: Record<string, unknown>;
}

/**
 * DOCU-664: Keys, die der Editor auf Feld-Ebene selbst verwaltet (und beim
 * Speichern neu erzeugt). Alles andere im API-Objekt gilt als unbekannt und
 * wird über `extra` unverändert durchgeschleift.
 */
const MANAGED_FIELD_KEYS = new Set([
  'id', 'type', 'label', 'uniqueKey', 'order', 'hint', 'initial', 'hidden', 'readonly',
  'selectOptions', 'numberOptions', 'photoPickerOptions', 'checkboxOptions', 'validators', 'allowPhoto',
  'folderLevel', 'exportHideWhenEmpty',
]);

/**
 * DOCU-664: Keys, die der Editor auf Level-Ebene selbst verwaltet.
 * exportFormat/exportTableMarker sind seit DOCU-664 bekannt und werden
 * explizit gemappt (nicht über `extra`).
 */
const MANAGED_LEVEL_KEYS = new Set([
  'id', 'name', 'description', 'fields', 'children', 'titleFieldId', 'subtitleFieldId',
  'exportFormat', 'exportTableMarker', 'exportKeepTogether', 'exportHideWhenEmpty',
]);

/**
 * DOCU-664: Sammelt alle Properties eines rohen API-Objekts ein, die der Editor
 * nicht selbst verwaltet, damit sie über einen Editier-Roundtrip erhalten bleiben.
 * Gibt `undefined` zurück, wenn es keine unbekannten Properties gibt.
 */
export function extractUnknownProps(
  source: Record<string, any> | null | undefined,
  managedKeys: Set<string>,
): Record<string, unknown> | undefined {
  if (!source) return undefined;
  const extra: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    if (!managedKeys.has(key)) {
      extra[key] = source[key];
    }
  }
  return Object.keys(extra).length > 0 ? extra : undefined;
}

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function generateFieldId(): string {
  return 'field_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Textfeld',
  textarea: 'Textbereich',
  number: 'Zahlenfeld',
  date: 'Datumsfeld',
  time: 'Zeitfeld',
  select: 'Auswahlfeld',
  checkbox: 'Checkbox',
  photo: 'Foto',
  signature: 'Unterschrift',
  divider: 'Trennlinie',
};

export function generateFieldLabel(type: string, existingFields: FormField[]): string {
  const baseLabel = FIELD_TYPE_LABELS[type] || type;
  const count = existingFields.filter(f => f.type === type).length;
  return `${baseLabel} ${count + 1}`;
}

export function generateLevelName(existingChildren: FormLevel[]): string {
  return `Bereich ${existingChildren.length + 1}`;
}

const TITLE_ELIGIBLE_TYPES = ['text', 'textarea', 'date', 'time', 'select', 'number'];

export function isTitleEligibleField(field: FormField): boolean {
  return TITLE_ELIGIBLE_TYPES.includes(field.type);
}

// DOCU-745: nur diese Feldtypen dürfen Ordnerschlüssel sein (Server-Regel
// DOCU-741: text/number/select auf Root-Ebene).
const FOLDER_ELIGIBLE_TYPES = ['text', 'number', 'select'];

export function isFolderEligibleField(field: FormField): boolean {
  return FOLDER_ELIGIBLE_TYPES.includes(field.type);
}

export function parseTemplateFieldId(value?: string | null): string | undefined {
  if (!value) return undefined;
  if (value.startsWith('#{') && value.endsWith('}')) {
    return value.slice(2, -1);
  }
  return undefined;
}

export function mapApiTypeToBuilderType(apiType: string): string {
  const typeMap: Record<string, string> = {
    text: 'text',
    number: 'number',
    date: 'date',
    time: 'time',
    select: 'select',
    checkbox: 'checkbox',
    photoPicker: 'photo',
    divider: 'divider',
    signature: 'signature',
  };
  return typeMap[apiType] || 'text';
}

export function mapBuilderTypeToApiType(builderType: string): string {
  const typeMap: Record<string, string> = {
    text: 'text',
    textarea: 'text',
    number: 'number',
    date: 'date',
    time: 'time',
    select: 'select',
    checkbox: 'checkbox',
    photo: 'photoPicker',
    divider: 'divider',
    signature: 'signature',
  };
  return typeMap[builderType] || 'text';
}

export function convertApiFieldsToFormFields(apiFields: ApiFormFieldJson[]): FormField[] {
  return apiFields.map((field) => {
    // DOCU-869: Der Wert steht je nach Alter des Datensatzes unter verschiedenen
    // Schlüsseln. Die API schreibt seit dem Fix kanonisch `minLength`/`maxLength`
    // bzw. `value` für Zahlengrenzen; davor schrieb sie die C#-Membernamen
    // (`MaxLength`), und der Builder selbst hat immer `value` geschickt. Wer nur
    // eine Schreibweise liest, zeigt ein leeres Feld an und löscht den Wert beim
    // nächsten Speichern — genau so ist der Defekt entstanden.
    const getValidatorValue = (type: string, ...keys: string[]): number | undefined => {
      const options = field.validators?.find((v) => v.type === type)?.options as
        | Record<string, unknown>
        | undefined;
      if (!options) return undefined;

      for (const key of keys) {
        const raw = options[key];
        if (raw === undefined || raw === null) continue;

        const num = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(num)) return num;
      }

      return undefined;
    };

    return {
      id: field.id || generateFieldId(),
      type: mapApiTypeToBuilderType(field.type || 'text'),
      label: field.label || '',
      hint: field.hint || '',
      required: field.validators?.some((v) => v.type === 'required') || false,
      validators: field.validators || [],
      options: field.selectOptions?.selectableOptions?.map((o) => o.label || '') || [],
      maxFiles: field.photoPickerOptions?.maxPhotos ?? undefined,
      showTimestamp: field.photoPickerOptions?.showTimestamp ?? undefined,
      showGeoStamp: field.photoPickerOptions?.showGeoStamp ?? undefined,
      writeGeoExif: field.photoPickerOptions?.writeGeoExif ?? undefined,
      // DOCU-815: Foto-Export-Optionen (noch nicht im generierten Modell)
      exportColumns: (field.photoPickerOptions as any)?.exportColumns ?? undefined,
      exportMaxImageCm: (field.photoPickerOptions as any)?.exportMaxImageCm ?? undefined,
      // DOCU-818: Feld bei leer ausblenden (top-level auf FormFieldJson)
      exportHideWhenEmpty: (field as any).exportHideWhenEmpty ?? undefined,
      allowAnnotation: field.photoPickerOptions?.allowAnnotation ?? undefined,
      minLength: getValidatorValue('minLength', 'minLength', 'MinLength', 'value'),
      maxLength: getValidatorValue('maxLength', 'maxLength', 'MaxLength', 'value'),
      // Zahlengrenzen heißen in App und API `value` — das ist hier das Sollformat.
      min: getValidatorValue('min', 'value', 'Value'),
      max: getValidatorValue('max', 'value', 'Value'),
      decimalPlaces: field.numberOptions?.decimalPlaces ?? undefined,
      useThousandsSeparator: field.numberOptions?.useThousandsSeparator ?? undefined,
      unit: field.numberOptions?.unit ?? undefined,
      initial: field.initial ?? null,
      hidden: field.hidden ?? null,
      // DOCU-669: Server-Property FormFieldJson.readonly (noch nicht im generierten Modell)
      readonly: (field as any).readonly ?? null,
      // DOCU-704/746: Export-Wertepärchen (Text + optionales SVG-Visual) für Boolean-Felder
      checkboxOptions: (field as any).checkboxOptions
        ? {
            trueLabel: (field as any).checkboxOptions.trueLabel ?? '',
            falseLabel: (field as any).checkboxOptions.falseLabel ?? '',
            trueSvg: (field as any).checkboxOptions.trueSvg ?? '',
            falseSvg: (field as any).checkboxOptions.falseSvg ?? '',
          }
        : undefined,
      // DOCU-745: Ordner-Schlüssel (Server: FormFieldJson.folderLevel)
      folderLevel: (field as any).folderLevel ?? null,
      // DOCU-664: unbekannte Feld-Properties durchschleifen
      extra: extractUnknownProps(field as Record<string, any>, MANAGED_FIELD_KEYS),
    };
  });
}

export function convertApiLevelsToFormLevels(apiLevels?: ApiFormLevelJson[] | null): FormLevel[] {
  if (!apiLevels || apiLevels.length === 0) {
    return [];
  }

  return apiLevels.map((apiLevel) => ({
    id: apiLevel.id || 'level_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
    name: apiLevel.name || '',
    description: apiLevel.description || '',
    titleFieldId: parseTemplateFieldId(apiLevel.titleFieldId),
    subtitleFieldId: parseTemplateFieldId(apiLevel.subtitleFieldId),
    // DOCU-664 / DOCU-625: DOCX-Export-Steuerung
    exportFormat: apiLevel.exportFormat ?? undefined,
    exportTableMarker: apiLevel.exportTableMarker ?? undefined,
    // DOCU-815/818: Prüfpunkt zusammenhalten / leeren Block ausblenden
    exportKeepTogether: apiLevel.exportKeepTogether ?? undefined,
    exportHideWhenEmpty: apiLevel.exportHideWhenEmpty ?? undefined,
    fields: convertApiFieldsToFormFields(apiLevel.fields || []),
    children: convertApiLevelsToFormLevels(apiLevel.children),
    isExpanded: true,
    // DOCU-664: unbekannte Level-Properties durchschleifen
    extra: extractUnknownProps(apiLevel, MANAGED_LEVEL_KEYS),
  }));
}

export function convertFieldToApiFormat(field: FormField, index: number = 0): any {
  const apiField: any = {
    // DOCU-664: unbekannte Properties zuerst, damit verwaltete Keys gewinnen
    ...(field.extra ?? {}),
    id: field.id,
    type: mapBuilderTypeToApiType(field.type),
    label: field.label,
    uniqueKey: field.id,
    order: index,
    hint: field.hint || '',
    validators: [],
  };

  if (field.initial) {
    apiField.initial = field.initial;
  }

  // DOCU-818: leeres Feld im Export ausblenden (nur explizit true persistieren;
  // ungesetzt = erbt das formularweite exportHideEmptyRows).
  if (field.exportHideWhenEmpty) {
    apiField.exportHideWhenEmpty = true;
  }

  if (field.required) {
    apiField.validators.push({ type: 'required' });
  }

  if (field.type === 'select' && field.options) {
    const opts = field.options
      .filter((o) => o != null && String(o).trim() !== '')
      .map((opt) => ({ label: opt, value: opt }));
    if (opts.length > 0) {
      apiField.selectOptions = { selectableOptions: opts };
    }
  }

  if (field.type === 'photo') {
    apiField.photoPickerOptions = {
      maxPhotos: field.maxFiles || 5,
      allowedSources: ['camera', 'gallery'],
      showTimestamp: field.showTimestamp ?? null,
      showGeoStamp: field.showGeoStamp ?? null,
      // DOCU-815: Kompakt-Export (leer = kein Opt-in, Renderer nutzt Default)
      exportColumns: field.exportColumns ?? null,
      exportMaxImageCm: field.exportMaxImageCm ?? null,
      // Additive override: only persist an explicit `true`. An unchecked box
      // stays `null` (inherit org default enablePhotoGeoExif) instead of being
      // coerced to `false`, which would silently override an org-wide opt-in to
      // OFF on any unrelated field edit.
      writeGeoExif: field.writeGeoExif ? true : null,
      // DOCU-859: same additive shape — the app treats null as off, so an
      // unchecked box needs no explicit false in the template JSON.
      allowAnnotation: field.allowAnnotation ? true : null,
    };
  }

  if (field.type === 'number') {
    if (field.min !== undefined) {
      apiField.validators.push({ type: 'min', options: { value: field.min } });
    }
    if (field.max !== undefined) {
      apiField.validators.push({ type: 'max', options: { value: field.max } });
    }
    if (field.decimalPlaces !== undefined || field.useThousandsSeparator || field.unit) {
      apiField.numberOptions = {
        decimalPlaces: field.decimalPlaces ?? 0,
        useThousandsSeparator: field.useThousandsSeparator ?? false,
        unit: field.unit || null,
      };
    }
  }

  if (field.type === 'text' || field.type === 'textarea') {
    // DOCU-869: Schlüssel muss `minLength`/`maxLength` heißen. Unter `value`
    // geschrieben, verwarf die API den Wert und setzte ihn auf 0 — die
    // Längenprüfung war dadurch überall wirkungslos.
    if (field.minLength && field.minLength > 0) {
      apiField.validators.push({ type: 'minLength', options: { minLength: field.minLength } });
    }
    if (field.maxLength && field.maxLength > 0) {
      apiField.validators.push({ type: 'maxLength', options: { maxLength: field.maxLength } });
    }
  }

  if (field.type !== 'photo' && field.allowPhoto) {
    apiField.allowPhoto = true;
    apiField.photoPickerOptions = { maxPhotos: 1 };
  }

  if (field.hidden) {
    apiField.hidden = true;
  }

  // DOCU-669: nur ein explizites true persistieren (nullable-Konvention wie
  // hidden/folderLevel) — ungesetzt heißt bearbeitbar.
  if (field.readonly) {
    apiField.readonly = true;
  }

  // DOCU-704/746: Boolean-Export-Wertepärchen (Text + optionales SVG-Visual).
  // Jede Gruppe (Text-/SVG-Paar) wird nur geschrieben, wenn mindestens eine Seite
  // gesetzt ist; ist gar nichts gesetzt, bleibt das Attribut weg (Export-Default).
  // Genau eine Seite leer ist zulässig (z. B. Zustimmungssatz bei true).
  if (field.type === 'checkbox' && field.checkboxOptions) {
    const trueLabel = (field.checkboxOptions.trueLabel ?? '').trim();
    const falseLabel = (field.checkboxOptions.falseLabel ?? '').trim();
    const trueSvg = (field.checkboxOptions.trueSvg ?? '').trim();
    const falseSvg = (field.checkboxOptions.falseSvg ?? '').trim();
    const checkboxOptions: Record<string, string> = {};
    if (trueLabel !== '' || falseLabel !== '') {
      checkboxOptions['trueLabel'] = trueLabel;
      checkboxOptions['falseLabel'] = falseLabel;
    }
    if (trueSvg !== '' || falseSvg !== '') {
      checkboxOptions['trueSvg'] = trueSvg;
      checkboxOptions['falseSvg'] = falseSvg;
    }
    if (Object.keys(checkboxOptions).length > 0) {
      apiField.checkboxOptions = checkboxOptions;
    }
  }

  // DOCU-745: Ordner-Schlüssel nur schreiben, wenn gesetzt (nullable-Konvention).
  if (field.folderLevel != null) {
    apiField.folderLevel = field.folderLevel;
  }

  return apiField;
}

export function convertLevelToApiFormat(level: FormLevel): any {
  const result: any = {
    // DOCU-664: unbekannte Properties zuerst, damit verwaltete Keys gewinnen
    ...(level.extra ?? {}),
    id: level.id,
    name: level.name,
    description: level.description || '',
    fields: level.fields.map((f, index) => convertFieldToApiFormat(f, index)),
    children: level.children.map((child) => convertLevelToApiFormat(child)),
  };
  if (level.titleFieldId) {
    result.titleFieldId = `#{${level.titleFieldId}}`;
  }
  if (level.subtitleFieldId) {
    result.subtitleFieldId = `#{${level.subtitleFieldId}}`;
  }
  // DOCU-664 / DOCU-625: DOCX-Export-Steuerung.
  // keyvalue ist der implizite Default → nur table/list explizit schreiben.
  if (level.exportFormat && level.exportFormat !== 'keyvalue') {
    result.exportFormat = level.exportFormat;
  }
  const marker = level.exportTableMarker?.trim();
  if (marker) {
    result.exportTableMarker = marker;
  }
  // DOCU-815/818: nur explizit gesetzte Flags schreiben (Default = aus).
  if (level.exportKeepTogether) {
    result.exportKeepTogether = true;
  }
  if (level.exportHideWhenEmpty) {
    result.exportHideWhenEmpty = true;
  }
  return result;
}
